import { compact, isEmpty, includes, uniqBy, maxBy } from 'lodash';
import { createSelector } from 'reselect';
import { ContentSource } from 'constants/tracking/content-sources';
import { stateName } from './constants';
import { filterMatchingSizeGroups, filterMatchingSizeIds, isStatusFilterVisible, isColorFilterVisible, isBrandFilterVisible, isSizeFilterVisible, isVideoGameRatingFilterVisible } from './utils';

const localState = state => state[stateName];

export const getDataDtos = state => localState(state).dtos;
export const getData = state => localState(state).data;
export const getSearchSession = state => localState(state).searchSession;
export const getIsInCatalog = state => localState(state).isInCatalog;
export const getFilters = state => localState(state).filters;
export const getQuery = state => getFilters(state).query;
export const getBrandIds = state => getFilters(state).brandIds;
export const getCatalogIds = state => getFilters(state).catalogIds;
export const getColorIds = state => getFilters(state).colorIds;
export const getSizeIds = state => getFilters(state).sizeIds;
export const getMaterialIds = state => getFilters(state).materialIds;
export const getVideoGameRatingIds = state => getFilters(state).videoGameRatingIds;
export const getStatusIds = state => getFilters(state).statusIds;
export const getPriceFrom = state => getFilters(state).priceFrom;
export const getPriceTo = state => getFilters(state).priceTo;
export const getCurrency = state => getFilters(state).currency;
export const getSortBy = state => getFilters(state).sortBy;
export const getIsPopularCatalog = state => getFilters(state).isPopularCatalog;
export const getIsPopularInCatalog = state => getFilters(state).disableSearchSaving;
export const getIsPersonalizationDisabled = state => getFilters(state).isPersonalizationDisabled;
export const getCatalogFrom = state => localState(state).filters.catalogFrom;
export const getSizeGroupIds = state => getData(state).sizeGroups.ids;
export const getCatalogById = (state, id) => getData(state).catalogs.byId[id];
export const getBrandById = (state, id) => getData(state).brands.byId[id];
export const getColorById = (state, id) => getData(state).colors.byId[id];
export const getSizeById = (state, id) => getData(state).sizes.byId[id];
export const getSizeGroupById = (state, id) => getData(state).sizeGroups.byId[id];
export const getStatusById = (state, id) => getData(state).statuses.byId[id];
export const getVideoGameRatingById = (state, id) => getData(state).videoGameRatings.byId[id];
export const getAllColors = state => compact(getData(state).colors.ids.map(id => getColorById(state, id)));
export const getAllStatuses = state => compact(getData(state).statuses.ids.map(id => getStatusById(state, id)));
export const getAllSizeGroups = state => compact(getData(state).sizeGroups.ids.map(sizeGroupId => getSizeGroupById(state, sizeGroupId)));
export const getAllCatalogs = state => compact(getData(state).catalogs.ids.map(id => getCatalogById(state, id)));
export const getAllVideoGameRatings = state => compact(getData(state).videoGameRatings.ids.map(id => getVideoGameRatingById(state, id)));
export const getCatalogMap = state => getData(state).catalogs.byId;
export const getSortedBrands = (state, customSelectedBrands) => {
  const brandIds = [...getData(state).brands.ids];
  const selectedBrands = customSelectedBrands || getBrandIds(state);
  selectedBrands.forEach(selectedBrandId => {
    if (includes(brandIds, selectedBrandId)) return;
    brandIds.unshift(selectedBrandId);
  });
  return compact(brandIds.map(id => getBrandById(state, id)));
};
export const getSelectedBrands = state => compact(getBrandIds(state).map(brandId => getBrandById(state, brandId)));
export const getSelectedCatalogs = state => compact(getCatalogIds(state).map(catalogId => getCatalogById(state, catalogId)));
export const getSelectedColors = state => compact(getColorIds(state).map(colorId => getColorById(state, colorId)));
export const getSelectedSizes = state => compact(getSizeIds(state).map(sizeId => getSizeById(state, sizeId)));
export const getSelectedStatuses = state => compact(getStatusIds(state).map(statusId => getStatusById(state, statusId)));
export const getSelectedVideoGameRatings = state => compact(getVideoGameRatingIds(state).map(colorId => getVideoGameRatingById(state, colorId)));
export const getSelectedFilters = function getSelectedFilters(state) {
  let includeQuery = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  const query = getQuery(state);
  const brands = getSelectedBrands(state);
  const catalogs = getSelectedCatalogs(state);
  const colors = getSelectedColors(state);
  const sizes = getSelectedSizes(state);
  const videoGameRatings = getSelectedVideoGameRatings(state);
  const statuses = getSelectedStatuses(state);
  const priceFrom = getPriceFrom(state);
  const priceTo = getPriceTo(state);
  const selectedFilters = [];
  catalogs.forEach(catalog => {
    selectedFilters.push({
      name: 'catalogIds',
      label: catalog.title,
      value: catalog.id
    });
  });

  if (!isEmpty(query) && includeQuery) {
    selectedFilters.push({
      name: 'query',
      label: String(query),
      value: String(query)
    });
  }

  brands.forEach(brand => {
    if (!brand) return;
    selectedFilters.push({
      name: 'brandIds',
      label: brand.title,
      value: brand.id
    });
  });
  sizes.forEach(size => {
    if (!size) return;
    selectedFilters.push({
      name: 'sizeIds',
      label: size.title,
      value: size.id
    });
  });
  colors.forEach(color => {
    selectedFilters.push({
      name: 'colorIds',
      label: color.title,
      value: color.id
    });
  });
  videoGameRatings.forEach(rating => {
    selectedFilters.push({
      name: 'videoGameRatingIds',
      label: rating.title,
      value: rating.id
    });
  });
  statuses.forEach(status => {
    selectedFilters.push({
      name: 'statusIds',
      label: status.title,
      value: status.id
    });
  });

  if (!isEmpty(priceFrom)) {
    selectedFilters.push({
      name: 'priceFrom',
      value: String(priceFrom)
    });
  }

  if (!isEmpty(priceTo)) {
    selectedFilters.push({
      name: 'priceTo',
      value: String(priceTo)
    });
  }

  return selectedFilters;
};
export const getSelectedFiltersCount = _ref => {
  let {
    includeQuery = true
  } = _ref;
  return state => getSelectedFilters(state, includeQuery).length;
};
export const getIsSizeFilterVisible = createSelector(getSelectedCatalogs, isSizeFilterVisible);
export const getIsBrandFilterVisible = createSelector(getSelectedCatalogs, isBrandFilterVisible);
export const getIsColorFilterVisible = createSelector(getSelectedCatalogs, isColorFilterVisible);
export const getIsStatusFilterVisible = createSelector(getSelectedCatalogs, isStatusFilterVisible);
export const getIsVideoGameRatingFilterVisible = createSelector(getSelectedCatalogs, isVideoGameRatingFilterVisible);
export const getSelectedFilterNamesWithoutQuery = createSelector(getSelectedFilters, filters => uniqBy(filters, 'name').map(_ref2 => {
  let {
    name
  } = _ref2;
  return name;
}).filter(name => name !== 'query'));
export const getRootCatalogs = createSelector(getAllCatalogs, catalogs => catalogs.filter(catalog => (catalog === null || catalog === void 0 ? void 0 : catalog.depth) === 0));
export const getSelectedCatalogsWithRoots = createSelector(getSelectedCatalogs, getRootCatalogs, (selectedCatalogs, rootCatalogs) => selectedCatalogs.length ? selectedCatalogs : compact(rootCatalogs));
export const getSelectedCatalogSizeGroups = createSelector(getAllSizeGroups, getSelectedCatalogsWithRoots, filterMatchingSizeGroups);
export const getSelectedCatalogSizeIds = createSelector(getSelectedCatalogSizeGroups, getSizeIds, filterMatchingSizeIds);
export const getDeepestSelectedCatalog = createSelector(getSelectedCatalogs, selectedCatalogs => maxBy(selectedCatalogs, catalog => catalog.depth));
export const getCatalogsForBreadcrumbs = state => {
  const deepestCatalog = getDeepestSelectedCatalog(state);
  if (!deepestCatalog) return [];
  let catalog = deepestCatalog;
  const result = [];

  while (catalog) {
    result.push(catalog);
    catalog = getCatalogById(state, Number(catalog.parentId));
  }

  return compact(result.reverse());
};
export const getSubcatalogs = createSelector(getDeepestSelectedCatalog, getAllCatalogs, getRootCatalogs, (deepestSelected, allCatalogs, rootCatalogs) => {
  if (deepestSelected) {
    const {
      catalogIds
    } = deepestSelected;
    return allCatalogs.filter(catalog => (catalog === null || catalog === void 0 ? void 0 : catalog.id) && catalogIds.includes(catalog.id));
  }

  return rootCatalogs;
});
export const getSearchCorrelationId = state => getSearchSession(state).correlationId;
export const getSearchSessionId = state => getSearchSession(state).sessionId;
export const getGlobalSearchSessionId = state => getSearchSession(state).globalSearchSessionId;
export const getIsSearchSessionStale = state => getSearchSession(state).isStale;
export const getCatalogContentSource = createSelector(getQuery, getIsPopularInCatalog, (query, isPopularCatalog) => {
  if (query) return ContentSource.Search;
  if (isPopularCatalog) return ContentSource.PopularItems;
  return ContentSource.Catalog;
});
export const getSizeCategories = createSelector([getRootCatalogs, getSizeGroupIds], (catalogs, groupIds) => compact(catalogs).filter(_ref3 => {
  let {
    sizeGroupIds
  } = _ref3;
  return !!sizeGroupIds.length;
}).map(_ref4 => {
  let {
    title,
    code,
    sizeGroupIds
  } = _ref4;
  return {
    title,
    subtitle: '',
    code,
    empty_selection: '',
    size_groups: groupIds.filter(id => sizeGroupIds.includes(id))
  };
}));
export const getDynamicFiltersState = state => localState(state).dynamicFilters;
export const getDynamicFiltersDataState = createSelector(getDynamicFiltersState, _ref5 => {
  let {
    dynamicFilters
  } = _ref5;
  return dynamicFilters;
});
export const getDynamicFiltersTypes = createSelector(getDynamicFiltersDataState, _ref6 => {
  let {
    ids
  } = _ref6;
  return ids;
});
export const getDynamicFiltersById = createSelector(getDynamicFiltersDataState, _ref7 => {
  let {
    byId
  } = _ref7;
  return byId;
});
export const getDynamicFiltersByType = type => createSelector(getDynamicFiltersById, byId => byId[type]);
export const getDynamicFilters = createSelector(getDynamicFiltersTypes, getDynamicFiltersById, (types, byType) => compact(types.map(type => byType[type])));
export const getSelectedDynamicFiltersDataState = createSelector(getDynamicFiltersState, _ref8 => {
  let {
    selectedDynamicFilters
  } = _ref8;
  return selectedDynamicFilters;
});
export const getSelectedDynamicFiltersTypes = createSelector(getSelectedDynamicFiltersDataState, _ref9 => {
  let {
    ids
  } = _ref9;
  return ids;
});
export const getSelectedDynamicFiltersByType = type => createSelector(getSelectedDynamicFiltersDataState, _ref10 => {
  let {
    byId
  } = _ref10;
  return byId[type];
});
export const getSelectedDynamicFiltersById = createSelector(getSelectedDynamicFiltersDataState, _ref11 => {
  let {
    byId
  } = _ref11;
  return byId;
});
export const getSelectedDynamicFilters = createSelector(getSelectedDynamicFiltersTypes, getSelectedDynamicFiltersById, (types, byType) => compact(types.map(type => byType[type])));
export const getSelectedDefaultDynamicFiltersDataState = createSelector(getDynamicFiltersState, _ref12 => {
  let {
    selectedDefaultFilters
  } = _ref12;
  return selectedDefaultFilters;
});
export const getSelectedDefaultDynamicFiltersTypes = createSelector(getSelectedDefaultDynamicFiltersDataState, _ref13 => {
  let {
    ids
  } = _ref13;
  return ids;
});
export const getSelectedDefultDynamicFiltersByType = type => createSelector(getSelectedDefaultDynamicFiltersDataState, _ref14 => {
  let {
    byId
  } = _ref14;
  return byId[type];
});
export const getSelectedDefaultDynamicFiltersById = createSelector(getSelectedDefaultDynamicFiltersDataState, _ref15 => {
  let {
    byId
  } = _ref15;
  return byId;
});
export const getSelectedDefaultDynamicFilters = createSelector(getSelectedDefaultDynamicFiltersTypes, getSelectedDefaultDynamicFiltersById, (types, byType) => compact(types.map(type => byType[type])));
export const getTemporarySelectedDynamicFiltersDataState = createSelector(getDynamicFiltersState, _ref16 => {
  let {
    temporarySelectedDynamicFilters
  } = _ref16;
  return temporarySelectedDynamicFilters;
});
export const getTemporarySelectedDynamicFiltersTypes = createSelector(getTemporarySelectedDynamicFiltersDataState, _ref17 => {
  let {
    ids
  } = _ref17;
  return ids;
});
export const getTemporarySelectedDynamicFiltersByType = type => createSelector(getTemporarySelectedDynamicFiltersDataState, _ref18 => {
  let {
    byId
  } = _ref18;
  return byId[type];
});
export const getTemporarySelectedDynamicFiltersById = createSelector(getTemporarySelectedDynamicFiltersDataState, _ref19 => {
  let {
    byId
  } = _ref19;
  return byId;
});
export const getTemporarySelectedDynamicFilters = createSelector(getTemporarySelectedDynamicFiltersTypes, getTemporarySelectedDynamicFiltersById, (types, byType) => compact(types.map(type => byType[type])));
export const getTemporaryCatalogIds = createSelector(getDynamicFiltersState, _ref20 => {
  let {
    temporaryCatalogIds
  } = _ref20;
  return temporaryCatalogIds;
});
export const getTemporarySizeIds = createSelector(getDynamicFiltersState, _ref21 => {
  let {
    temporarySizeIds
  } = _ref21;
  return temporarySizeIds;
});
export const getTemporaryBrandIds = createSelector(getDynamicFiltersState, _ref22 => {
  let {
    temporaryBrandIds
  } = _ref22;
  return temporaryBrandIds;
});
export const getTemporaryColorIds = createSelector(getDynamicFiltersState, _ref23 => {
  let {
    temporaryColorIds
  } = _ref23;
  return temporaryColorIds;
});
export const getTemporaryPriceRange = createSelector(getDynamicFiltersState, _ref24 => {
  let {
    temporaryPriceRage
  } = _ref24;
  return temporaryPriceRage;
});
export const getFilterSearchQuery = createSelector(getDynamicFiltersState, _ref25 => {
  let {
    filterSearchQuery
  } = _ref25;
  return filterSearchQuery;
});
export const getDynamicFiltersUi = createSelector(getDynamicFiltersState, _ref26 => {
  let {
    ui
  } = _ref26;
  return ui;
});
export const getDynamicFiltersUiErrors = createSelector(getDynamicFiltersUi, _ref27 => {
  let {
    errors
  } = _ref27;
  return errors;
});
export const getDynamicFiltersUiState = createSelector(getDynamicFiltersUi, _ref28 => {
  let {
    uiState
  } = _ref28;
  return uiState;
});
export const getDynamicSupportedFilters = createSelector(getDynamicFiltersState, _ref29 => {
  let {
    supportedFilters
  } = _ref29;
  return supportedFilters;
});